import React from 'react';

import config from '../../config';
import { auth } from '../../firebase';
import { useSelector } from 'react-redux';
import { navigateTop } from '../routing/router-utils';
import { useDistanceMatrix, getFormattedAddress } from '../googlemaps/google-maps-utils';
import { formatPhoneNumber } from 'react-phone-number-input';
import useIntercomLogging from '../intercom/useIntercomLogging';
import OrderTypeToggle from '../common/OrderTypeToggle';
import PlaceOrderItems from '../checkout/PlaceOrderItems';
import CurrentTimeProvider from '../timeSlots/CurrentTimeProvider';
import PickupSlotsProvider from '../timeSlots/PickupSlotsProvider';
import DisplayPickupSlots from '../timeSlots/DisplayPickupSlots';
import OrderPickupButton from './OrderPickupButton';
import RegisteredUserInfo from '../checkoutUserFlow/RegisteredUserInfo';
import GuestForm from '../checkoutUserFlow/GuestForm';
import SignInInline from '../registration/SignInInline';
import OrderPickupAsGuest  from './OrderPickupAsGuest';
import DisplayBulkDiscount from '../checkout/DisplayBulkDiscount';
import ErrorBoundary from '../common/ErrorBoundary';
import PropTypes from 'prop-types';

import styles from '../checkout/PlaceOrder.module.css';

const PlaceOrderPickup = ({
  currentUser,
  dispensaryId,
  dispensaryName,
  dispensaryTelNumber,
  dispensaryLocation,
  minPickupOrder,
  dispensaryLogoPath,
  handleSignUpClick,
  handlePickupClick
}) => {
  
  const [showSignIn, setShowSignIn] = React.useState();

  // Enable AeroPay?
  const aeroPayEnabled = config.AEROPAY_ENABLED;

  const delivery_address = useSelector(state => state.delivery_address);
  // trigger rerender on cart change
  const itemTotal = useSelector(state => state.total);

  const [distance, setDistance] = React.useState();
  const [pickupAddress, setPickupAddress] = React.useState();
  const [noDiscountFound, setNoDiscountFound] = React.useState();
  const [dispensaryPhone, setDispensaryPhone] = React.useState();

  const userAddress = getFormattedAddress(delivery_address);
  const dispensaryAddress = getFormattedAddress(dispensaryLocation);

  // Launching with pay-in-store
  const [payInStore, setPayInStore] = React.useState(!aeroPayEnabled); 

  // Google Maps API
  useDistanceMatrix(userAddress, dispensaryAddress, setDistance);

  const OrderSection = ({title, children}) => {
    return (
      <ErrorBoundary>
        <div className={styles.section}>
          { title && (
            <div className={styles.sectionTitle}>{title}</div>
          )}
          <div className={styles.sectionInner}>
            {children}
          </div>
        </div>
      </ErrorBoundary>
    );
  }
  
  React.useEffect(() => {
    const dispensaryAddress = getFormattedAddress(dispensaryLocation);
    setPickupAddress(dispensaryAddress);
  }, [dispensaryLocation]);  

  React.useEffect(() =>{
    if(dispensaryTelNumber) {
      setDispensaryPhone(
        dispensaryTelNumber.indexOf('+') !== -1 ? dispensaryTelNumber : `+${dispensaryTelNumber}`
      );   
    }
  }, [dispensaryTelNumber]);

  // Ensure bulk discount recalc
  React.useEffect(() => {
    setNoDiscountFound(false);  
  }, [itemTotal]);

  // Track user starting checkout
  useIntercomLogging(auth.currentUser, 'Checkout_Pickup', true);

  return (
    <>
      <OrderSection>
        <div className={styles.logoWrap}>
          { dispensaryLogoPath &&
            <img className={styles.logo} alt="" height="30" src={dispensaryLogoPath} />
          }
          <div className={styles.dispensaryName}>
            <div className={styles.orderFrom}>PICKUP ORDER FROM</div>
            <div className={styles.orderStore} onClick={() => {navigateTop(`/dispensary/${dispensaryId}`)}}>
              {dispensaryName}
            </div>
          </div>
        </div>
      </OrderSection>

      <OrderSection title="CUSTOMER">
        { currentUser && !currentUser.isAnonymous
          ? <RegisteredUserInfo registeredUser={currentUser} />
          : showSignIn
            ? <SignInInline onCancel={() => setShowSignIn(false)} /> 
            : <GuestForm 
                signInClick={() => setShowSignIn(true)}
                signUpClick={handleSignUpClick} />
        } 
      </OrderSection>

      <OrderSection title="TYPE">
        <OrderTypeToggle inBody withStyle={{margin:0}} isPickup handlePickupClick={handlePickupClick} />
      </OrderSection>
      
      <OrderSection title="TIME">
        <ErrorBoundary>
          <CurrentTimeProvider>
            <PickupSlotsProvider dispensaryId={dispensaryId}>
              <DisplayPickupSlots dispensaryId={dispensaryId} dispensaryName={dispensaryName} />
            </PickupSlotsProvider>
          </CurrentTimeProvider>
        </ErrorBoundary>
      </OrderSection>
      
      <OrderSection title="ADDRESS">
        <span>
          { pickupAddress &&
            `${pickupAddress}`
          }
        </span>
        { distance &&
          <b>{` (${distance}`})</b>
        }
      </OrderSection>
      
      <OrderSection title="PHONE NUMBER">
        <span>{formatPhoneNumber(dispensaryPhone)}</span>
      </OrderSection>
      <OrderSection title="PAYMENT">
        <> 
          { aeroPayEnabled &&
            <div className={styles.radioWrapper} onClick={() => setPayInStore(false)}>
              <input id="payNow" name="paymentType" type="radio" value="payNow" defaultChecked={!payInStore} />
              <label htmlFor="payNow">Pay using your AeroPay-linked bank account</label>
            </div>
          }
          <div className={styles.radioWrapper} onClick={() => setPayInStore(true)}>
            <input id="payInStore" name="paymentType" type="radio" value="payInStore" defaultChecked={payInStore}/>
            <label htmlFor="payInStore">Pay in-store</label>
          </div>
        </>
      </OrderSection>
      <OrderSection title="SUMMARY">
        <PlaceOrderItems />
      </OrderSection>
      { !noDiscountFound &&
        <OrderSection title="BULK DISCOUNTS">
          <DisplayBulkDiscount handleNoDiscount={setNoDiscountFound} />
        </OrderSection>
      }
      <OrderSection>
        { currentUser && !currentUser.isAnonymous
          ? <OrderPickupButton
              dispensaryId={dispensaryId}
              dispensaryName={dispensaryName}
              minPickupOrder={minPickupOrder}
              isPayInStore={payInStore} />
          : <OrderPickupAsGuest  
              dispensaryId={dispensaryId}
              dispensaryName={dispensaryName}
              minPickupOrder={minPickupOrder}
            />
        }    
      </OrderSection>
    </>
   
  )
}

PlaceOrderPickup.propTypes = {
  currentUser: PropTypes.object,
  dispensaryId: PropTypes.string.isRequired,
  dispensaryName: PropTypes.string.isRequired,
  dispensaryTelNumber: PropTypes.string.isRequired,
  dispensaryLocation: PropTypes.object.isRequired,
  minPickupOrder: PropTypes.number.isRequired,
  dispensaryLogoPath: PropTypes.string,
  handleSignUpClick: PropTypes.func.isRequired,
  handlePickupClick: PropTypes.func.isRequired
};

export default PlaceOrderPickup;